import { useRef, useState, useCallback, useEffect } from "react";
import { Play, Loader2 } from "lucide-react";
import { useInView } from "@/hooks/useInView";

interface VideoCardProps {
  coverImage: string;
  videoSrc: string;
  shouldPause?: boolean;
  onPlay?: () => void;
}

const VideoCard = ({ coverImage, videoSrc, shouldPause, onPlay }: VideoCardProps) => {
  const [inViewRef, isInView] = useInView({ rootMargin: "100px" });
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [videoMounted, setVideoMounted] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Pause when another video starts playing
  useEffect(() => {
    if (shouldPause && isPlaying) {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
      setIsPlaying(false);
      setIsLoading(false);
    }
  }, [shouldPause, isPlaying]);

  const handleTogglePlay = useCallback(async () => {
    if (!videoSrc) return;

    if (isPlaying) {
      if (videoRef.current) {
        videoRef.current.pause();
      }
      setIsPlaying(false);
      return;
    }

    // Mount video element on first interaction
    if (!videoMounted) setVideoMounted(true);

    setIsLoading(true);

    // Wait for next frame so the video element is in the DOM
    requestAnimationFrame(async () => {
      if (!videoRef.current) {
        setIsLoading(false);
        return;
      }
      try {
        await videoRef.current.play();
        setIsPlaying(true);
        setIsLoading(false);
        onPlay?.();
      } catch {
        setIsPlaying(false);
        setIsLoading(false);
      }
    });
  }, [videoSrc, isPlaying, videoMounted, onPlay]);

  // Skeleton when not in view
  if (!isInView) {
    return (
      <div
        ref={inViewRef}
        className="relative aspect-[9/16] rounded-xl overflow-hidden bg-muted animate-pulse"
      />
    );
  }

  return (
    <div
      ref={inViewRef}
      className="relative aspect-[9/16] rounded-xl overflow-hidden shadow-lg select-none cursor-pointer"
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      onClick={handleTogglePlay}
    >
      {/* Cover image */}
      <div
        className={`absolute inset-0 bg-cover bg-center transition-opacity duration-300 ${isPlaying ? "opacity-0" : "opacity-100"}`}
        style={
          coverImage
            ? { backgroundImage: `url(${coverImage})` }
            : { background: "linear-gradient(135deg, hsl(var(--muted)) 0%, hsl(var(--accent)) 100%)" }
        }
      />

      {/* Video element - only mounted after first interaction */}
      {videoMounted && videoSrc && (
        <video
          ref={videoRef}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${isPlaying ? "opacity-100" : "opacity-0"}`}
          src={videoSrc}
          muted
          loop
          playsInline
          preload="none"
          controlsList="nodownload"
          disablePictureInPicture
          onContextMenu={(e) => e.preventDefault()}
        />
      )}

      {/* Transparent overlay */}
      <div className="absolute inset-0 z-10" />

      {/* Loading spinner */}
      {isLoading && !isPlaying && (
        <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
          <div className="rounded-full bg-black/40 p-3">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </div>
        </div>
      )}

      {/* Play icon */}
      {!isPlaying && !isLoading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
          <div className="rounded-full bg-black/40 p-3">
            <Play className="w-8 h-8 text-white fill-white" />
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoCard;
